import { MODULE_GRID } from "../data/balance.js";

/** 포트/이동 방향 (그리드 y는 화면 아래로 증가). */
export type Direction = "up" | "right" | "down" | "left";

export interface GridPosition {
  x: number;
  y: number;
}

export interface ModuleGridLane {
  start: GridPosition;
  fire: GridPosition;
}

/**
 * 그리드에 배치된 모듈 인스턴스 (지시문 §9·§18). instanceId가 배치 키 — 같은 정의의 다른 인스턴스도 동시 배치 가능.
 * definitionId/shapeId는 렌더·효과 조회용으로 함께 denormalize 저장(인벤토리 역참조 없이 그리드 렌더 가능).
 */
export interface PlacedModule {
  x: number;
  y: number;
  instanceId: string;
  definitionId: string;
  /** 연결 모양 (moduleShapes SHAPE_CATALOG id, 방향 명시). 탭 시 효과 shapePool 내 다음 모양으로 순환. */
  shapeId: string;
  /** 슬롯 점유 모양 (single/vert2/horz2). 인벤토리 인스턴스에서 denormalize. anchor=(x,y). */
  footprintId: string;
}

/** 모듈 조립 그리드 상태 (sparse — unlockedCells/placedModules 목록). */
export interface ModuleGridState {
  width: number;
  height: number;
  /** 레거시 단일 배선 호환용. 신규 코드는 lanes[0]을 우선 사용한다. */
  start: GridPosition;
  fire: GridPosition;
  /** 우주선별 독립 배선. 각 배선은 Start→Fire 경로와 기본 발사구 1개를 뜻한다. */
  lanes?: ModuleGridLane[];
  /** 우주선 형태상 존재하지 않는 칸. 해금/배치/경로 전파 대상에서 제외한다. */
  blockedCells?: GridPosition[];
  /** 우주선 기본 소유 슬롯 수. 해금 비용 purchasedCount 계산의 기준점이다. */
  baseUnlockedCount?: number;
  unlockedCells: GridPosition[];
  placedModules: PlacedModule[];
}

export const DIRECTIONS: readonly Direction[] = ["up", "right", "down", "left"];

const OPPOSITE: Record<Direction, Direction> = {
  up: "down",
  down: "up",
  left: "right",
  right: "left",
};

const DELTA: Record<Direction, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

export function opposite(d: Direction): Direction {
  return OPPOSITE[d];
}

export function dirDelta(d: Direction): { dx: number; dy: number } {
  return DELTA[d];
}

/** 초기 해금 칸 — Start/Fire 행 5칸만 열어 새 게임의 기본 직선 물길을 만든다. */
export function buildInitialUnlockedCells(): GridPosition[] {
  // `as const` 리터럴 narrowing을 피하려 number로 명시.
  const x0: number = MODULE_GRID.initialUnlock.x0;
  const y0: number = MODULE_GRID.initialUnlock.y0;
  const uw: number = MODULE_GRID.initialUnlock.w;
  const uh: number = MODULE_GRID.initialUnlock.h;

  const cells: GridPosition[] = [];
  for (let y = y0; y < y0 + uh; y++) {
    for (let x = x0; x < x0 + uw; x++) cells.push({ x, y });
  }
  return cells;
}

/** 초기 해금 칸 수 (Start/Fire 제외) — 해금 비용 계산 기준 (purchasedCount = 보유 − 초기). */
export const INITIAL_UNLOCKED_COUNT = buildInitialUnlockedCells().length;

/** 초기 그리드 상태 — 전체 5×5, 중앙행 5칸 해금(S→F 연결 시작), 배치 모듈 없음. */
export function createInitialGridState(): ModuleGridState {
  const width: number = MODULE_GRID.width;
  const height: number = MODULE_GRID.height;
  const startX: number = MODULE_GRID.start.x;
  const startY: number = MODULE_GRID.start.y;
  const fireX: number = MODULE_GRID.fire.x;
  const fireY: number = MODULE_GRID.fire.y;

  return {
    width,
    height,
    start: { x: startX, y: startY },
    fire: { x: fireX, y: fireY },
    lanes: [{ start: { x: startX, y: startY }, fire: { x: fireX, y: fireY } }],
    blockedCells: [],
    baseUnlockedCount: buildInitialUnlockedCells().length,
    unlockedCells: buildInitialUnlockedCells(),
    placedModules: [],
  };
}
